import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast, Toaster } from 'sonner';
import { 
  Users, Plus, Trash2, Check, AlertCircle, 
  Loader2, ChevronRight, ChevronLeft, Edit2, Globe,
  FileText, Shield, CheckCircle2, ArrowRight, Info
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ TRANSLATIONS ============
const TRANSLATIONS = {
  fr: {
    // Welcome screen
    welcomeTitle: 'Bienvenue',
    welcomeSubtitle: 'Pré-enregistrement de votre séjour',
    welcomeIntro: 'Avant votre arrivée, nous avons besoin de quelques informations sur les voyageurs.',
    whyTitle: 'Pourquoi ces informations ?',
    whyText: 'La législation marocaine exige l\'enregistrement de tous les voyageurs séjournant dans un établissement touristique. Ces informations sont transmises aux autorités locales.',
    yourStay: 'Votre séjour',
    arrival: 'Arrivée',
    departure: 'Départ',
    travelers: 'Voyageurs',
    adult: 'adulte',
    adults: 'adultes',
    child: 'enfant',
    children: 'enfants',
    startBtn: 'Commencer',
    
    // Passengers screen
    passengersTitle: 'Voyageurs',
    passengersSubtitle: 'Ajoutez les informations de chaque personne',
    passengerNum: 'Voyageur',
    addPassenger: 'Ajouter un voyageur',
    noPassengers: 'Aucun voyageur ajouté',
    tapToAdd: 'Appuyez sur le bouton ci-dessous pour commencer',
    complete: 'Complet',
    incomplete: 'À compléter',
    continueBtn: 'Continuer',
    addMoreOrContinue: 'Ajoutez d\'autres voyageurs ou continuez',
    passengersRequired: 'Ajoutez au moins un voyageur pour continuer',
    allPassengersRequired: 'Tous les champs obligatoires doivent être remplis pour chaque voyageur',
    minPassengersRequired: 'Vous devez enregistrer au moins {count} voyageur(s)',
    
    // Passenger form
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    nationality: 'Nationalité',
    birthDate: 'Date de naissance',
    passport: 'Passeport ou carte d\'identité',
    docNumber: 'Numéro du document',
    docExpiry: 'Date d\'expiration',
    required: 'obligatoire',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    nameRequired: 'Le prénom et le nom sont obligatoires',
    allFieldsRequired: 'Tous les champs obligatoires doivent être remplis',
    
    // Missing fields messages
    missingFirstName: 'prénom manquant',
    missingLastName: 'nom manquant',
    missingNationality: 'nationalité manquante',
    missingBirthDate: 'date de naissance manquante',
    missingDocNumber: 'numéro de document manquant',
    missingDocExpiry: 'date d\'expiration manquante',
    
    // Rules screen
    rulesTitle: 'Règlement',
    rulesSubtitle: 'Merci de prendre connaissance du règlement',
    rulesIntro: 'Pour que votre séjour se passe au mieux, voici quelques règles à respecter :',
    servicesTitle: 'Services disponibles',
    servicesIntro: 'Pendant votre séjour, vous pouvez profiter de :',
    acceptRules: 'J\'ai lu et j\'accepte le règlement',
    validateBtn: 'Valider et terminer',
    mustAcceptRules: 'Veuillez accepter le règlement pour continuer',
    checkinFrom: 'Arrivée à partir de',
    checkoutBefore: 'Départ avant',
    additionalInfo: 'Informations supplémentaires',
    additionalInfoPlaceholder: 'Numéro de vol, heure d\'arrivée estimée, demandes spéciales...',
    optional: 'optionnel',
    
    // Validation errors
    cannotContinue: 'Veuillez compléter les informations de tous les voyageurs',
    paxIncomplete: 'est incomplet',
    
    // Confirmation screen
    confirmTitle: 'C\'est terminé !',
    confirmSubtitle: 'Merci pour votre pré-enregistrement',
    confirmText: 'Vos informations ont été enregistrées avec succès. Nous avons hâte de vous accueillir !',
    confirmReminder: 'N\'oubliez pas d\'apporter vos documents d\'identité le jour de votre arrivée.',
    seeYouSoon: 'À bientôt !',
    
    // Common
    back: 'Retour',
    step: 'Étape',
    of: 'sur',
    loading: 'Chargement...',
    error: 'Une erreur est survenue',
    errorExpired: 'Ce lien n\'est plus valide',
    saving: 'Enregistrement...',
    saved: 'Enregistré !',
    connectionError: 'Erreur de connexion',
  },
  en: {
    // Welcome screen
    welcomeTitle: 'Welcome',
    welcomeSubtitle: 'Pre-registration for your stay',
    welcomeIntro: 'Before your arrival, we need some information about the travelers.',
    whyTitle: 'Why this information?',
    whyText: 'Moroccan law requires the registration of all travelers staying in a tourist establishment. This information is transmitted to local authorities.',
    yourStay: 'Your stay',
    arrival: 'Arrival',
    departure: 'Departure',
    travelers: 'Travelers',
    adult: 'adult',
    adults: 'adults',
    child: 'child',
    children: 'children',
    startBtn: 'Get started',
    
    // Passengers screen
    passengersTitle: 'Travelers',
    passengersSubtitle: 'Add information for each person',
    passengerNum: 'Traveler',
    addPassenger: 'Add a traveler',
    noPassengers: 'No travelers added yet',
    tapToAdd: 'Tap the button below to start',
    complete: 'Complete',
    incomplete: 'Incomplete',
    continueBtn: 'Continue',
    addMoreOrContinue: 'Add more travelers or continue',
    passengersRequired: 'Add at least one traveler to continue',
    allPassengersRequired: 'All required fields must be filled for each traveler',
    minPassengersRequired: 'You must register at least {count} traveler(s)',
    
    // Passenger form
    firstName: 'First name',
    lastName: 'Last name',
    nationality: 'Nationality',
    birthDate: 'Date of birth',
    passport: 'Passport or ID card',
    docNumber: 'Document number',
    docExpiry: 'Expiry date',
    required: 'required',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    nameRequired: 'First and last name are required',
    allFieldsRequired: 'All required fields must be filled',
    
    // Missing fields messages
    missingFirstName: 'first name missing',
    missingLastName: 'last name missing',
    missingNationality: 'nationality missing',
    missingBirthDate: 'birth date missing',
    missingDocNumber: 'document number missing',
    missingDocExpiry: 'expiry date missing',
    
    // Rules screen
    rulesTitle: 'House rules',
    rulesSubtitle: 'Please read our house rules',
    rulesIntro: 'To ensure a pleasant stay, please respect these rules:',
    servicesTitle: 'Available services',
    servicesIntro: 'During your stay, you can enjoy:',
    acceptRules: 'I have read and accept the rules',
    validateBtn: 'Validate and finish',
    mustAcceptRules: 'Please accept the rules to continue',
    checkinFrom: 'Check-in from',
    checkoutBefore: 'Check-out before',
    additionalInfo: 'Additional information',
    additionalInfoPlaceholder: 'Flight number, estimated arrival time, special requests...',
    optional: 'optional',
    
    // Validation errors
    cannotContinue: 'Please complete all traveler information',
    paxIncomplete: 'is incomplete',
    
    // Confirmation screen
    confirmTitle: 'All done!',
    confirmSubtitle: 'Thank you for your pre-registration',
    confirmText: 'Your information has been saved successfully. We look forward to welcoming you!',
    confirmReminder: 'Don\'t forget to bring your ID documents on the day of your arrival.',
    seeYouSoon: 'See you soon!',
    
    // Common
    back: 'Back',
    step: 'Step',
    of: 'of',
    loading: 'Loading...',
    error: 'An error occurred',
    errorExpired: 'This link is no longer valid',
    saving: 'Saving...',
    saved: 'Saved!',
    connectionError: 'Connection error',
  }
};

// ============ COUNTRIES ============
const COUNTRIES = [
  { code: 'FR', name: { fr: 'France', en: 'France' }, flag: '🇫🇷' },
  { code: 'MA', name: { fr: 'Maroc', en: 'Morocco' }, flag: '🇲🇦' },
  { code: 'BE', name: { fr: 'Belgique', en: 'Belgium' }, flag: '🇧🇪' },
  { code: 'CH', name: { fr: 'Suisse', en: 'Switzerland' }, flag: '🇨🇭' },
  { code: 'DE', name: { fr: 'Allemagne', en: 'Germany' }, flag: '🇩🇪' },
  { code: 'GB', name: { fr: 'Royaume-Uni', en: 'United Kingdom' }, flag: '🇬🇧' },
  { code: 'ES', name: { fr: 'Espagne', en: 'Spain' }, flag: '🇪🇸' },
  { code: 'IT', name: { fr: 'Italie', en: 'Italy' }, flag: '🇮🇹' },
  { code: 'NL', name: { fr: 'Pays-Bas', en: 'Netherlands' }, flag: '🇳🇱' },
  { code: 'US', name: { fr: 'États-Unis', en: 'United States' }, flag: '🇺🇸' },
  { code: 'CA', name: { fr: 'Canada', en: 'Canada' }, flag: '🇨🇦' },
  { code: 'PT', name: { fr: 'Portugal', en: 'Portugal' }, flag: '🇵🇹' },
  { code: 'DZ', name: { fr: 'Algérie', en: 'Algeria' }, flag: '🇩🇿' },
  { code: 'TN', name: { fr: 'Tunisie', en: 'Tunisia' }, flag: '🇹🇳' },
  { code: 'AE', name: { fr: 'Émirats arabes unis', en: 'United Arab Emirates' }, flag: '🇦🇪' },
  { code: 'SA', name: { fr: 'Arabie saoudite', en: 'Saudi Arabia' }, flag: '🇸🇦' },
  { code: 'JP', name: { fr: 'Japon', en: 'Japan' }, flag: '🇯🇵' },
  { code: 'CN', name: { fr: 'Chine', en: 'China' }, flag: '🇨🇳' },
  { code: 'AU', name: { fr: 'Australie', en: 'Australia' }, flag: '🇦🇺' },
  { code: 'BR', name: { fr: 'Brésil', en: 'Brazil' }, flag: '🇧🇷' },
];

const detectLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  return browserLang?.startsWith('fr') ? 'fr' : 'en';
};

// ============ STEP COMPONENTS ============

// Header with progress
const StepHeader = ({ step, totalSteps, businessName, businessLogo, lang, onLangChange }) => (
  <div className="bg-white border-b px-4 py-3 sticky top-0 z-10">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        {businessLogo ? (
          <img src={businessLogo} alt="" className="h-10 w-10 object-contain rounded-lg" />
        ) : (
          <div className="h-10 w-10 bg-amber-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">{(businessName || 'R')[0]}</span>
          </div>
        )}
        <span className="font-semibold text-amber-900">{businessName}</span>
      </div>
      <button 
        onClick={onLangChange}
        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-sm"
        data-testid="language-switcher"
      >
        <Globe className="w-4 h-4" />
        {lang.toUpperCase()}
      </button>
    </div>
    {/* Progress bar */}
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4].map((s) => (
        <div 
          key={s}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            s <= step ? 'bg-amber-500' : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  </div>
);

// Step 1: Welcome
const WelcomeStep = ({ data, t, lang, dateLocale, onNext }) => (
  <div className="flex-1 overflow-y-auto">
    <div className="p-6 space-y-6">
      {/* Welcome message */}
      <div className="text-center pt-4">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.welcomeTitle}</h1>
        <p className="text-lg text-amber-700">{t.welcomeSubtitle}</p>
      </div>
      
      <p className="text-gray-600 text-center">{t.welcomeIntro}</p>
      
      {/* Why section */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-blue-900 mb-1">{t.whyTitle}</p>
            <p className="text-sm text-blue-700">{t.whyText}</p>
          </div>
        </div>
      </div>
      
      {/* Reservation info */}
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
        <p className="font-semibold text-amber-900 mb-3">{t.yourStay}</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t.arrival}</span>
            <span className="font-medium">{format(new Date(data.check_in), 'EEEE d MMMM yyyy', { locale: dateLocale })}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t.departure}</span>
            <span className="font-medium">{format(new Date(data.check_out), 'EEEE d MMMM yyyy', { locale: dateLocale })}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t.travelers}</span>
            <span className="font-medium">
              {data.adults} {data.adults > 1 ? t.adults : t.adult}
              {data.children > 0 && `, ${data.children} ${data.children > 1 ? t.children : t.child}`}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    {/* Bottom button */}
    <div className="p-4 bg-white border-t mt-auto">
      <Button 
        onClick={onNext}
        className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700"
        data-testid="start-btn"
      >
        {t.startBtn}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Helper to check if a PAX has all required fields for COMPLETED status
const isPaxComplete = (pax) => {
  return pax.first_name?.trim() && 
         pax.last_name?.trim() && 
         pax.nationality && 
         pax.birth_date && 
         pax.document_number?.trim() && 
         pax.document_expiry;
};

// Get missing fields for a PAX
const getMissingFields = (pax, t) => {
  const missing = [];
  if (!pax.first_name?.trim()) missing.push(t.missingFirstName);
  if (!pax.last_name?.trim()) missing.push(t.missingLastName);
  if (!pax.nationality) missing.push(t.missingNationality);
  if (!pax.birth_date) missing.push(t.missingBirthDate);
  if (!pax.document_number?.trim()) missing.push(t.missingDocNumber);
  if (!pax.document_expiry) missing.push(t.missingDocExpiry);
  return missing;
};

// Step 2: Passengers
const PassengersStep = ({ paxList, expectedPax, t, lang, onAddPax, onEditPax, onDeletePax, onNext, onBack }) => {
  const allComplete = paxList.length >= expectedPax && paxList.every(isPaxComplete);
  const hasMinPax = paxList.length >= expectedPax;
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4">
        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">{t.passengersTitle}</h2>
          <p className="text-gray-600">{t.passengersSubtitle}</p>
          <p className={`text-sm mt-1 ${paxList.length >= expectedPax ? 'text-green-600' : 'text-amber-600'}`}>
            {paxList.length} / {expectedPax} {t.travelers.toLowerCase()}
          </p>
        </div>
        
        {/* Validation warning */}
        {paxList.length > 0 && !allComplete && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium">{t.cannotContinue}</p>
            <ul className="mt-2 text-xs text-amber-700 space-y-1">
              {paxList.map((pax, idx) => {
                const missing = getMissingFields(pax, t);
                if (missing.length === 0) return null;
                const name = pax.first_name && pax.last_name 
                  ? `${pax.first_name} ${pax.last_name}`
                  : `${t.passengerNum} ${idx + 1}`;
                return (
                  <li key={idx}>• {name}: {missing.join(', ')}</li>
                );
              })}
              {!hasMinPax && (
                <li>• {t.minPassengersRequired.replace('{count}', expectedPax)}</li>
              )}
            </ul>
          </div>
        )}
        
        {/* Passenger list */}
        <div className="space-y-3">
          {paxList.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-1">{t.noPassengers}</p>
              <p className="text-sm text-gray-400">{t.tapToAdd}</p>
            </div>
          ) : (
            paxList.map((pax, index) => {
              const complete = isPaxComplete(pax);
              const missing = getMissingFields(pax, t);
              return (
                <div 
                  key={pax.id || `new-${index}`}
                  className={`p-4 rounded-xl border-2 ${
                    complete 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      complete ? 'bg-green-500' : 'bg-amber-500'
                    }`}>
                      {complete ? <Check className="w-5 h-5" /> : index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">
                        {pax.first_name && pax.last_name 
                          ? `${pax.first_name} ${pax.last_name}`
                          : `${t.passengerNum} ${index + 1}`
                        }
                      </p>
                      <p className="text-xs text-gray-500">
                        {complete ? (
                          <span className="text-green-600">✓ {t.complete}</span>
                        ) : (
                          <span className="text-amber-600">{t.incomplete} ({missing.length})</span>
                        )}
                        {pax.nationality && ` • ${COUNTRIES.find(c => c.code === pax.nationality)?.flag || pax.nationality}`}
                      </p>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => onEditPax(index)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {pax.added_by_guest && (
                      <Button size="icon" variant="ghost" onClick={() => onDeletePax(index)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          
          {/* Add button */}
          <button
            onClick={onAddPax}
            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
            data-testid="add-pax-btn"
          >
            <Plus className="w-5 h-5" />
            {t.addPassenger}
          </button>
        </div>
        
        {paxList.length > 0 && (
          <p className="text-center text-sm text-gray-500 mt-4">{t.addMoreOrContinue}</p>
        )}
      </div>
      
      {/* Bottom buttons */}
      <div className="p-4 bg-white border-t flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1 h-12">
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t.back}
        </Button>
        <Button 
          onClick={onNext}
          disabled={!allComplete}
          className={`flex-1 h-12 ${allComplete ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-300'}`}
          data-testid="continue-btn"
        >
          {t.continueBtn}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Passenger Form (full screen)
const PassengerForm = ({ pax, index, t, lang, onSave, onCancel, onDelete }) => {
  const [form, setForm] = useState({
    first_name: pax?.first_name || '',
    last_name: pax?.last_name || '',
    nationality: pax?.nationality || '',
    birth_date: pax?.birth_date || '',
    document_number: pax?.document_number || '',
    document_expiry: pax?.document_expiry || '',
    added_by_guest: pax?.added_by_guest ?? true,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!form.first_name?.trim()) newErrors.first_name = true;
    if (!form.last_name?.trim()) newErrors.last_name = true;
    if (!form.nationality) newErrors.nationality = true;
    if (!form.birth_date) newErrors.birth_date = true;
    if (!form.document_number?.trim()) newErrors.document_number = true;
    if (!form.document_expiry) newErrors.document_expiry = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error(t.allFieldsRequired);
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <span className="flex-1 text-center font-semibold">{t.passengerNum} {index + 1}</span>
        <div className="w-10" />
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* First name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.firstName} <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.first_name}
            onChange={(e) => {
              setForm({ ...form, first_name: e.target.value });
              setErrors({ ...errors, first_name: false });
            }}
            placeholder="Jean"
            className={`h-12 text-lg ${errors.first_name ? 'border-red-500' : ''}`}
            data-testid="pax-first-name"
          />
        </div>

        {/* Last name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.lastName} <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.last_name}
            onChange={(e) => {
              setForm({ ...form, last_name: e.target.value });
              setErrors({ ...errors, last_name: false });
            }}
            placeholder="Dupont"
            className={`h-12 text-lg ${errors.last_name ? 'border-red-500' : ''}`}
            data-testid="pax-last-name"
          />
        </div>

        {/* Nationality */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.nationality} <span className="text-red-500">*</span>
          </label>
          <div className={`grid grid-cols-4 gap-2 ${errors.nationality ? 'border border-red-500 rounded-lg p-2' : ''}`}>
            {COUNTRIES.slice(0, 12).map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  setForm({ ...form, nationality: country.code });
                  setErrors({ ...errors, nationality: false });
                }}
                className={`p-2 rounded-lg border-2 text-center transition-all ${
                  form.nationality === country.code 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-gray-200 bg-white'
                }`}
              >
                <span className="text-2xl">{country.flag}</span>
                <p className="text-[10px] text-gray-600 truncate mt-0.5">{country.name[lang]}</p>
              </button>
            ))}
          </div>
          {/* Other countries */}
          <select
            className="w-full mt-2 p-3 border rounded-lg bg-white text-gray-700"
            value={COUNTRIES.slice(0, 12).find(c => c.code === form.nationality) ? '' : form.nationality}
            onChange={(e) => {
              setForm({ ...form, nationality: e.target.value });
              setErrors({ ...errors, nationality: false });
            }}
          >
            <option value="">Autre pays...</option>
            {COUNTRIES.slice(12).map(country => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name[lang]}
              </option>
            ))}
          </select>
        </div>

        {/* Birth date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.birthDate} <span className="text-red-500">*</span>
          </label>
          <Input
            type="date"
            value={form.birth_date}
            onChange={(e) => {
              setForm({ ...form, birth_date: e.target.value });
              setErrors({ ...errors, birth_date: false });
            }}
            className={`h-12 text-lg ${errors.birth_date ? 'border-red-500' : ''}`}
            data-testid="pax-birth-date"
          />
        </div>

        {/* Document number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.docNumber} <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.document_number}
            onChange={(e) => {
              setForm({ ...form, document_number: e.target.value.toUpperCase() });
              setErrors({ ...errors, document_number: false });
            }}
            placeholder="AB1234567"
            className={`h-12 text-lg font-mono ${errors.document_number ? 'border-red-500' : ''}`}
            data-testid="pax-doc-number"
          />
        </div>

        {/* Document expiry date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.docExpiry} <span className="text-red-500">*</span>
          </label>
          <Input
            type="date"
            value={form.document_expiry}
            onChange={(e) => {
              setForm({ ...form, document_expiry: e.target.value });
              setErrors({ ...errors, document_expiry: false });
            }}
            className={`h-12 text-lg ${errors.document_expiry ? 'border-red-500' : ''}`}
            data-testid="pax-doc-expiry"
          />
        </div>

        {/* Delete button */}
        {pax?.added_by_guest && pax?.id && (
          <Button 
            variant="outline" 
            className="w-full text-red-500 border-red-200"
            onClick={() => onDelete(index)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t.delete}
          </Button>
        )}
      </div>

      {/* Bottom button */}
      <div className="p-4 bg-white border-t">
        <Button 
          onClick={handleSave}
          className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700"
          data-testid="save-pax-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          {t.save}
        </Button>
      </div>
    </div>
  );
};

// Step 3: Rules
const RulesStep = ({ rulesText, extrasText, checkinTime, checkoutTime, additionalNotes, setAdditionalNotes, rulesAccepted, setRulesAccepted, t, onNext, onBack, saving }) => (
  <div className="flex-1 flex flex-col overflow-hidden">
    <div className="flex-1 overflow-y-auto p-4">
      {/* Title */}
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Shield className="w-6 h-6 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{t.rulesTitle}</h2>
        <p className="text-gray-600">{t.rulesSubtitle}</p>
      </div>
      
      {/* Check-in / Check-out times */}
      {(checkinTime || checkoutTime) && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          {checkinTime && (
            <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
              <p className="text-xs text-green-600 mb-1">{t.checkinFrom}</p>
              <p className="text-lg font-bold text-green-700">{checkinTime}</p>
            </div>
          )}
          {checkoutTime && (
            <div className="bg-red-50 rounded-xl p-3 border border-red-200 text-center">
              <p className="text-xs text-red-600 mb-1">{t.checkoutBefore}</p>
              <p className="text-lg font-bold text-red-700">{checkoutTime}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Rules */}
      {rulesText && (
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-3">{t.rulesIntro}</p>
          <div className="bg-gray-50 rounded-xl p-4 border">
            <p className="text-gray-700 whitespace-pre-line text-sm leading-relaxed">{rulesText}</p>
          </div>
        </div>
      )}
      
      {/* Services */}
      {extrasText && (
        <div className="mb-6">
          <p className="font-medium text-gray-900 mb-2">{t.servicesTitle}</p>
          <p className="text-sm text-gray-600 mb-3">{t.servicesIntro}</p>
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <p className="text-gray-700 whitespace-pre-line text-sm leading-relaxed">{extrasText}</p>
          </div>
        </div>
      )}
      
      {/* Additional notes - free text field */}
      <div className="mb-6">
        <p className="font-medium text-gray-900 mb-2">{t.additionalInfo}</p>
        <p className="text-xs text-gray-500 mb-2">({t.optional})</p>
        <textarea
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder={t.additionalInfoPlaceholder}
          className="w-full p-3 border rounded-xl bg-white text-gray-700 text-sm min-h-[100px] resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          data-testid="additional-notes"
        />
      </div>
      
      {/* Accept checkbox */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <Checkbox
          id="accept-rules"
          checked={rulesAccepted}
          onCheckedChange={setRulesAccepted}
          className="mt-0.5"
          data-testid="accept-rules-checkbox"
        />
        <label htmlFor="accept-rules" className="text-sm text-gray-700 cursor-pointer">
          {t.acceptRules}
        </label>
      </div>
    </div>
    
    {/* Bottom buttons */}
    <div className="p-4 bg-white border-t flex gap-3">
      <Button variant="outline" onClick={onBack} className="flex-1 h-12">
        <ChevronLeft className="w-4 h-4 mr-1" />
        {t.back}
      </Button>
      <Button 
        onClick={onNext}
        disabled={!rulesAccepted || saving}
        className="flex-1 h-12 bg-green-600 hover:bg-green-700"
        data-testid="validate-btn"
      >
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
        ) : (
          <Check className="w-5 h-5 mr-2" />
        )}
        {saving ? t.saving : t.validateBtn}
      </Button>
    </div>
  </div>
);

// Step 4: Confirmation
const ConfirmationStep = ({ data, t, dateLocale }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
      <CheckCircle2 className="w-10 h-10 text-green-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.confirmTitle}</h2>
    <p className="text-lg text-green-700 mb-6">{t.confirmSubtitle}</p>
    <p className="text-gray-600 mb-6">{t.confirmText}</p>
    
    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-left w-full max-w-sm mb-6">
      <p className="text-sm text-amber-800">
        <strong>{t.confirmReminder}</strong>
      </p>
    </div>
    
    <p className="text-xl font-semibold text-amber-700">{t.seeYouSoon}</p>
  </div>
);

// ============ MAIN COMPONENT ============
export default function PreCheckinPage() {
  const { token } = useParams();
  const [lang, setLang] = useState(detectLanguage);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [paxList, setPaxList] = useState([]);
  const [step, setStep] = useState(1);
  const [editingIndex, setEditingIndex] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const t = useMemo(() => TRANSLATIONS[lang], [lang]);
  const dateLocale = lang === 'fr' ? fr : enUS;

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/precheckin/public/${token}`);
      const result = await response.json();
      
      if (!response.ok) {
        setError(result.error || t.error);
        setLoading(false);
        return;
      }
      
      // Handle both old and new API structure
      const reservation = result.reservation || result;
      setData({
        ...result,
        guest_name: reservation.guest_name || result.guest_name,
        check_in: reservation.check_in || result.check_in,
        check_out: reservation.check_out || result.check_out,
        adults: reservation.adults || result.adults,
        children: reservation.children || result.children,
        infants: reservation.infants || result.infants,
        total_pax: reservation.total_pax || result.total_pax,
        precheckin_status: reservation.precheckin_status || result.precheckin_status
      });
      setPaxList(result.pax_list || reservation.pax_list || []);
      
      // If already completed, go to confirmation
      const status = reservation.precheckin_status || result.precheckin_status;
      if (status === 'COMPLETED' && (result.pax_list?.length > 0 || reservation.pax_list?.length > 0)) {
        setStep(4);
      }
      
      setLoading(false);
    } catch (err) {
      setError(t.connectionError);
      setLoading(false);
    }
  };

  const addPax = () => {
    setEditingIndex(paxList.length);
  };

  // Auto-save PAX to backend after local state update
  const autoSavePax = async (updatedPaxList) => {
    try {
      const response = await fetch(`${API_URL}/api/precheckin/public/${token}/pax`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          pax_list: updatedPaxList,
          additional_notes: additionalNotes.trim() || null
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Update with server-returned data (includes IDs for new PAX)
        setPaxList(result.pax_list);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Auto-save failed:', err);
      return false;
    }
  };

  const savePax = async (index, updatedPax) => {
    const updated = [...paxList];
    if (index >= paxList.length) {
      updated.push({ ...updatedPax, isNew: true, added_by_guest: true });
    } else {
      updated[index] = { ...paxList[index], ...updatedPax };
    }
    setPaxList(updated);
    setEditingIndex(null);
    
    // Auto-save to backend (non-blocking with feedback)
    setSaving(true);
    const success = await autoSavePax(updated);
    setSaving(false);
    
    if (success) {
      toast.success(t.saved);
    } else {
      toast.error(t.connectionError);
    }
  };

  const deletePax = async (index) => {
    const pax = paxList[index];
    
    if (pax.id && pax.added_by_guest) {
      try {
        await fetch(`${API_URL}/api/precheckin/public/${token}/pax/${pax.id}`, {
          method: 'DELETE'
        });
      } catch (err) {
        toast.error(t.connectionError);
        return;
      }
    }
    
    setPaxList(paxList.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  const handleSubmit = async () => {
    if (!rulesAccepted) {
      toast.error(t.mustAcceptRules);
      return;
    }
    
    // Validate all passengers have all required fields
    const expectedPax = data.total_pax || (data.adults + data.children);
    if (paxList.length < expectedPax) {
      toast.error(t.minPassengersRequired.replace('{count}', expectedPax));
      return;
    }
    
    for (const pax of paxList) {
      if (!pax.first_name?.trim() || !pax.last_name?.trim()) {
        toast.error(t.nameRequired);
        return;
      }
      if (!pax.nationality || !pax.birth_date || !pax.document_number?.trim() || !pax.document_expiry) {
        toast.error(t.allPassengersRequired);
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/precheckin/public/${token}/pax`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          pax_list: paxList,
          additional_notes: additionalNotes.trim() || null
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        toast.error(result.error || t.error);
        setSaving(false);
        return;
      }
      
      setPaxList(result.pax_list);
      setStep(4);
      toast.success(t.saved);
    } catch (err) {
      toast.error(t.connectionError);
    } finally {
      setSaving(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-amber-600 mx-auto mb-3" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 text-center max-w-sm shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t.error}</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Editing passenger form
  if (editingIndex !== null) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <PassengerForm
          pax={paxList[editingIndex]}
          index={editingIndex}
          t={t}
          lang={lang}
          onSave={(updated) => savePax(editingIndex, updated)}
          onCancel={() => setEditingIndex(null)}
          onDelete={deletePax}
        />
      </>
    );
  }

  const expectedPax = data.total_pax || (data.adults + data.children);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col" data-testid="precheckin-page">
      <Toaster position="top-center" richColors />
      
      {step < 4 && (
        <StepHeader
          step={step}
          totalSteps={4}
          businessName={data.business_name}
          businessLogo={data.business_logo}
          lang={lang}
          onLangChange={() => setLang(lang === 'fr' ? 'en' : 'fr')}
        />
      )}
      
      {step === 1 && (
        <WelcomeStep
          data={data}
          t={t}
          lang={lang}
          dateLocale={dateLocale}
          onNext={() => setStep(2)}
        />
      )}
      
      {step === 2 && (
        <PassengersStep
          paxList={paxList}
          expectedPax={expectedPax}
          t={t}
          lang={lang}
          onAddPax={addPax}
          onEditPax={setEditingIndex}
          onDeletePax={deletePax}
          onNext={() => {
            if (paxList.length === 0) {
              toast.error(t.passengersRequired);
              return;
            }
            // Check if all pax are complete
            const allComplete = paxList.every(pax => 
              pax.first_name?.trim() && 
              pax.last_name?.trim() && 
              pax.nationality && 
              pax.birth_date && 
              pax.document_number?.trim() && 
              pax.document_expiry
            );
            if (!allComplete) {
              toast.error(t.allPassengersRequired);
              return;
            }
            if (paxList.length < expectedPax) {
              toast.error(t.minPassengersRequired.replace('{count}', expectedPax));
              return;
            }
            setStep(3);
          }}
          onBack={() => setStep(1)}
        />
      )}
      
      {step === 3 && (
        <RulesStep
          rulesText={data.rules?.[lang] || data.rules_text || ''}
          extrasText={data.extras?.[lang] || data.extras_text || ''}
          checkinTime={data.checkin_time}
          checkoutTime={data.checkout_time}
          additionalNotes={additionalNotes}
          setAdditionalNotes={setAdditionalNotes}
          rulesAccepted={rulesAccepted}
          setRulesAccepted={setRulesAccepted}
          t={t}
          onNext={handleSubmit}
          onBack={() => setStep(2)}
          saving={saving}
        />
      )}
      
      {step === 4 && (
        <ConfirmationStep
          data={data}
          t={t}
          dateLocale={dateLocale}
        />
      )}
    </div>
  );
}
